import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Clock, Gavel, ShoppingBag, XCircle } from "lucide-react";
import { useApp } from "../context/AppContext";
import { useAuth, authHeaders, errCode } from "../context/AuthContext";
import LuxDialog from "./ui/LuxDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ICON = { approved: CheckCircle2, pending: Clock, rejected: XCircle, revoked: XCircle, none: ShoppingBag };
const CHIP = { approved: "chip-ok", pending: "chip-warn", rejected: "chip-bad", revoked: "chip-bad", none: "" };

export default function BuyerAccessCard() {
  const { t } = useApp();
  const { refreshUser } = useAuth();
  const [info, setInfo] = useState(null);
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/souq/buyer-access/me`, { headers: authHeaders() }).then((r) => setInfo(r.data)).catch(() => setInfo({ status: "none" }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/souq/buyer-access/request`, { note }, { headers: authHeaders() });
      toast.success(t("ba.sent")); setOpen(false); setNote(""); load(); refreshUser?.();
    } catch (e) { toast.error(t(`err.${errCode(e)}`)); } finally { setBusy(false); }
  };

  if (!info) return null;
  const Icon = ICON[info.status] || ShoppingBag;
  const canRequest = ["none", "rejected", "revoked"].includes(info.status);
  return (
    <section className="ba-card glass-card" data-testid="buyer-access-card">
      <div className="ba-head">
        <Icon size={18} className="ba-ic" />
        <div className="ba-text">
          <h3 className="dash-panel-title ba-title">{t("ba.title")}</h3>
          <p className="ba-desc">{t("ba.desc")}</p>
        </div>
        <span className={`chip ${CHIP[info.status]} ba-status`} data-testid={`buyer-access-status-${info.status}`}>{t(`ba.status.${info.status}`)}</span>
      </div>
      {info.reason && info.status !== "approved" && <p className="ba-reason" data-testid="buyer-access-reason">{t("ba.reason")}: {info.reason}</p>}
      <div className="ba-actions">
        {canRequest && <button className="btn-lux-gold" onClick={() => setOpen(true)} data-testid="buyer-access-request-button"><Gavel size={14} /> {t("ba.request")}</button>}
        {info.status === "approved" && <Link to="/souq/dashboard" className="btn-lux-ghost" data-testid="buyer-access-dashboard-link"><ShoppingBag size={14} /> {t("ba.myBuying")}</Link>}
        {info.status === "approved" && <Link to="/souq" className="btn-lux-ghost" data-testid="buyer-access-souq-link"><Gavel size={14} /> {t("ba.browse")}</Link>}
      </div>
      <LuxDialog open={open} onOpenChange={setOpen} title={t("ba.request")} description={t("ba.requestDesc")} icon={Gavel}
        confirmLabel={t("ba.send")} cancelLabel={t("admin.cancel")} onConfirm={submit} busy={busy} testId="buyer-access-dialog">
        <label className="po-form"><span>{t("ba.note")}</span>
          <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} data-testid="buyer-access-note" /></label>
      </LuxDialog>
    </section>
  );
}
